import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pairsApi, Pair } from '../api/pairs';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../i18n';

export default function PairManagement() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [newPartnerEmail, setNewPartnerEmail] = useState('');
  const [createError, setCreateError] = useState('');
  const [showForm, setShowForm] = useState(false);

  const { data: pairs = [], isLoading } = useQuery({
    queryKey: ['my-pairs'],
    queryFn: () => pairsApi.listMine().then((r) => r.data),
    enabled: !!user,
  });

  const createMutation = useMutation({
    mutationFn: (email: string) => pairsApi.create({ player2_email: email }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-pairs'] });
      setNewPartnerEmail('');
      setShowForm(false);
      setCreateError('');
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      setCreateError(e.response?.data?.detail ?? t('pairs.createError'));
    },
  });

  const dissolveMutation = useMutation({
    mutationFn: (id: number) => pairsApi.dissolve(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['my-pairs'] }),
  });

  const acceptMutation = useMutation({
    mutationFn: (id: number) => pairsApi.accept(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['my-pairs'] }),
  });

  const declineMutation = useMutation({
    mutationFn: (id: number) => pairsApi.decline(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['my-pairs'] }),
  });

  function pairStatus(pair: Pair) {
    if (pair.status === 'active') return { label: t('pairs.statusActive'), cls: 'bg-green-100 text-green-700' };
    if (pair.status === 'pending') return { label: t('pairs.statusPending'), cls: 'bg-yellow-100 text-yellow-700' };
    return { label: t('pairs.statusDissolved'), cls: 'bg-gray-100 text-gray-500' };
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('pairs.title')}</h1>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="btn btn-primary btn-sm">
            {t('pairs.newPair')}
          </button>
        )}
      </div>

      {showForm && (
        <div className="card mb-6">
          <h2 className="text-base font-semibold mb-3">{t('pairs.invitePartner')}</h2>
          {createError && (
            <div className="text-red-600 text-sm mb-3">{createError}</div>
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              value={newPartnerEmail}
              onChange={(e) => setNewPartnerEmail(e.target.value)}
              placeholder={t('pairs.partnerEmail')}
              className="input flex-1"
            />
            <div className="flex gap-2">
              <button
                onClick={() => createMutation.mutate(newPartnerEmail)}
                disabled={createMutation.isPending || !newPartnerEmail}
                className="btn btn-primary flex-1 sm:flex-none"
              >
                {createMutation.isPending ? '...' : t('pairs.invite')}
              </button>
              <button onClick={() => { setShowForm(false); setCreateError(''); }} className="btn btn-secondary flex-1 sm:flex-none">
                {t('pairs.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => <div key={i} className="card animate-pulse h-16" />)}
        </div>
      ) : pairs.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p className="mb-2">{t('pairs.noPairs')}</p>
          <p className="text-sm">{t('pairs.inviteHint')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pairs.map((pair) => {
            const st = pairStatus(pair);
            const isCreator = user?.id === pair.player1_id;
            const creator = pair.player1;
            const partner = isCreator ? pair.player2 : pair.player1;
            const creatorName = creator ? `${creator.first_name} ${creator.last_name}` : '?';
            const partnerLabel = partner
              ? `${partner.first_name} ${partner.last_name}`
              : pair.status === 'pending' ? t('pairs.invitationPending') : '–';

            return (
              <div key={pair.id} className="card">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="space-y-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/users/${pair.player1_id}`} className="font-medium text-gray-900 hover:text-primary-600">{creatorName}</Link>
                      <span className="badge bg-orange-100 text-orange-700 text-xs">{t('pairs.creator')}</span>
                      <span className="text-gray-400">/</span>
                      {partner
                        ? <Link to={`/users/${isCreator ? pair.player2_id : pair.player1_id}`} className="font-medium text-gray-900 hover:text-primary-600">{partnerLabel}</Link>
                        : <span className="font-medium text-gray-400 italic">{partnerLabel}</span>
                      }
                      {pair.status === 'pending' && (
                        <span className="badge bg-yellow-100 text-yellow-700 text-xs">{t('pairs.confirmationPending')}</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400">
                      {t('pairs.created')} {new Date(pair.created_at).toLocaleDateString(getDateLocale())}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`badge ${st.cls}`}>{st.label}</span>
                    {pair.status === 'pending' && !isCreator && (
                      <>
                        <button
                          onClick={() => acceptMutation.mutate(pair.id)}
                          disabled={acceptMutation.isPending}
                          className="btn btn-primary btn-sm"
                        >
                          {t('pairs.accept', 'Annehmen')}
                        </button>
                        <button
                          onClick={() => declineMutation.mutate(pair.id)}
                          disabled={declineMutation.isPending}
                          className="btn btn-secondary btn-sm"
                        >
                          {t('pairs.decline', 'Ablehnen')}
                        </button>
                      </>
                    )}
                    {pair.status !== 'dissolved' && isCreator && (
                      <button
                        onClick={() => dissolveMutation.mutate(pair.id)}
                        disabled={dissolveMutation.isPending}
                        className="btn btn-danger btn-sm"
                      >
                        {t('pairs.dissolve')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
